import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { salesApi, usersApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { EditIcon, TrashIcon } from '../../utils/icons';
import { formatDate, timeAgo, formatCurrency } from '../../utils/formatters';
import Modal from '../../components/Modal';
import { SaleStageBadge } from './SaleList';
import './SaleDetail.css';

const SALE_STAGES = ['pending', 'approved', 'eligible_for_invoice', 'invoice_raised', 'settled'];
const STAGE_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  eligible_for_invoice: 'Eligible for Invoice',
  invoice_raised: 'Invoice Raised',
  settled: 'Settled',
};

const UNIT_TYPES = [
  { value: 'flat', label: 'Flat' },
  { value: 'shop', label: 'Shop' },
  { value: 'plot', label: 'Plot' },
];
const SALE_TYPES = [
  { value: 'fresh', label: 'Fresh' },
  { value: 'resale', label: 'Resale' },
];

export default function SaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [sale, setSale] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showStageConfirm, setShowStageConfirm] = useState(null);

  const fetchSale = useCallback(async () => {
    try {
      setError('');
      const res = await salesApi.get(id);
      setSale(res.data);
    } catch (err) {
      if (err.response?.status === 404) setError('Sale not found.');
      else if (err.response?.status === 403) setError('You do not have access to this sale.');
      else setError('Failed to load sale.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await salesApi.activity(id);
      setActivity(res.data);
    } catch {
      // Non-critical
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch {
      // Non-critical
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchSale(); fetchActivity(); fetchUsers(); }, [fetchSale, fetchActivity, fetchUsers]);

  const canEdit = () => {
    if (!sale) return false;
    if (currentUser?.role === 'admin') return true;
    // After approved, agent cannot edit
    const stageIdx = SALE_STAGES.indexOf(sale.stage);
    return stageIdx < 1 && sale.agent_id === currentUser?.id;
  };

  const canDelete = () => {
    if (currentUser?.role === 'admin') return true;
    return sale?.stage === 'pending' && sale?.agent_id === currentUser?.id;
  };

  const canAdvanceStage = () => currentUser?.role === 'admin';

  const handleStageAdvance = async (newStage) => {
    setShowStageConfirm(null);
    try {
      const res = await salesApi.advanceStage(id, newStage);
      setSale(res.data);
      fetchActivity();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update stage.');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await salesApi.addNote(id, newNote.trim());
      setNewNote('');
      fetchActivity();
    } catch {
      setError('Failed to add note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleEdited = () => { setShowEdit(false); fetchSale(); fetchActivity(); };
  const handleDeleted = () => { setShowDelete(false); navigate('/sales'); };

  if (loading) {
    return (
      <div className="sd-page">
        <div className="sd-loading"><div className="sd-loading-spinner" /><p>Loading sale...</p></div>
      </div>
    );
  }

  if (error && !sale) {
    return (
      <div className="sd-page">
        <div className="breadcrumb">
          <Link to="/sales" className="breadcrumb-link">Sales</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Error</span>
        </div>
        <div className="sd-error-card card">
          <p>{error}</p>
          <Link to="/sales" className="btn btn-ghost" style={{ marginTop: 12 }}>Back to sales</Link>
        </div>
      </div>
    );
  }

  if (!sale) return null;

  const currentStageIdx = SALE_STAGES.indexOf(sale.stage);

  return (
    <div className="sd-page">
      {/* Breadcrumb */}
      <div className="breadcrumb animate-fade-up">
        <Link to="/sales" className="breadcrumb-link">Sales</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">{sale.client_name}</span>
      </div>

      {error && <div className="sd-error animate-fade-up">{error}</div>}

      {/* Header */}
      <div className="sd-header animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="sd-identity">
          <div className="sd-avatar">
            {sale.client_name.split(' ').map(n => n[0]).slice(0, 2).join('')}
          </div>
          <div>
            <h1 className="sd-name">{sale.client_name}</h1>
            <div className="sd-meta">
              <span className="sd-pan">{sale.pan_number}</span>
              <SaleStageBadge stage={sale.stage} />
              <span className="sd-added">Added {formatDate(sale.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="sd-actions">
          {canEdit() && (
            <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
              <EditIcon /> Edit
            </button>
          )}
          {canDelete() && (
            <button className="btn btn-ghost btn-danger-text" onClick={() => setShowDelete(true)}>
              <TrashIcon /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Stage pipeline */}
      <div className="card sd-stage-card animate-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="stage-pipeline">
          {SALE_STAGES.map((stage, i) => {
            const isActive = sale.stage === stage;
            const isDone = i < currentStageIdx;
            const canClick = canAdvanceStage() && i > currentStageIdx;
            return (
              <React.Fragment key={stage}>
                <button
                  className={`stage-step ${isActive ? 'stage-step--active' : ''} ${isDone ? 'stage-step--done' : ''}`}
                  onClick={() => canClick && setShowStageConfirm(stage)}
                  disabled={!canClick}
                  style={canClick ? { cursor: 'pointer' } : {}}
                >
                  <span className="stage-step-dot">
                    {isDone ? <CheckIcon /> : i + 1}
                  </span>
                  <span className="stage-step-label">{STAGE_LABELS[stage]}</span>
                </button>
                {i < SALE_STAGES.length - 1 && (
                  <div className={`stage-connector ${i < currentStageIdx ? 'stage-connector--done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Body grid */}
      <div className="sd-grid animate-fade-up" style={{ animationDelay: '120ms' }}>
        {/* Left: Details */}
        <div className="sd-left">
          {/* Client info */}
          <div className="card">
            <h3 className="detail-section-title">Client Information</h3>
            <div className="detail-fields">
              <DetailField label="Client Name" value={sale.client_name} />
              <DetailField label="PAN Number" value={sale.pan_number} monospace />
              <DetailField label="Booking Date" value={sale.booking_date ? formatDate(sale.booking_date) : null} />
            </div>
          </div>

          {/* Project details */}
          <div className="card">
            <h3 className="detail-section-title">Project Details</h3>
            <div className="detail-fields">
              <DetailField label="Unit Type" value={sale.unit_type} />
              <DetailField label="Sale Type" value={sale.sale_type} />
              <DetailField label="Project Name" value={sale.project_name} />
              <DetailField label="Size" value={sale.size ? `${sale.size} sq. ft.` : null} />
              <DetailField label="Config" value={sale.config} />
              <DetailField label="Price/sq.ft." value={sale.price_per_sqft ? `${sale.price_per_sqft}` : null} />
              <DetailField label="PLC" value={sale.plc ? formatCurrency(sale.plc) : null} accent />
              <DetailField label="Other Charges" value={sale.other_charges ? formatCurrency(sale.other_charges) : null} accent />
              <DetailField label="Total Sales Value" value={formatCurrency(sale.total_sales_value)} accent />
            </div>
          </div>

          {/* Brokerage */}
          <div className="card">
            <h3 className="detail-section-title">Brokerage Details</h3>
            <div className="detail-fields">
              <DetailField label="Type" value={sale.brokerage_type === 'percentage' ? 'Percentage' : 'Lumpsum'} />
              {sale.brokerage_type === 'percentage' && (
                <DetailField label="Percentage" value={`${sale.brokerage_percentage}%`} />
              )}
              <DetailField label="Brokerage Amount" value={formatCurrency(sale.brokerage_amount)} accent />
              {sale.brokerage_remark && (
                <DetailField label="Remark" value={sale.brokerage_remark} />
              )}
              <DetailField label="Customer Passback" value={formatCurrency(sale.customer_passback)} />
              <DetailField label="Broker Passback" value={formatCurrency(sale.broker_passback)} />
              <DetailField label="Brokerage Earned" value={formatCurrency(sale.brokerage_earned)} accent />
              <DetailField label="Brokerage Received" value={formatCurrency(sale.brokerage_received)} />
            </div>
          </div>

          {/* Agent share */}
          <div className="card">
            <h3 className="detail-section-title">Agent Share</h3>
            <div className="detail-fields">
              <DetailField label="Type" value={sale.agent_share_type === 'percentage' ? 'Percentage' : 'Lumpsum'} />
              {sale.agent_share_type === 'percentage' && (
                <DetailField label="Percentage" value={`${sale.agent_share_percentage}%`} />
              )}
              <DetailField label="Agent Share Amount" value={formatCurrency(sale.agent_share_amount)} accent />
              {sale.agent_share_remark && (
                <DetailField label="Remark" value={sale.agent_share_remark} />
              )}
              {currentUser?.role === 'admin' && sale.mis > 0 && (
                <>
                  <DetailField label="MIS Deduction" value={`- ${formatCurrency(sale.mis)}`} />
                  <DetailField label="Net Agent Share" value={formatCurrency(sale.agent_share_amount - sale.mis)} accent />
                </>
              )}
            </div>
          </div>

          {/* Company share (admin only) */}
          {currentUser?.role === 'admin' && sale.company_share != null && (
            <div className="card sd-company-card">
              <h3 className="detail-section-title">Company Share</h3>
              <div className="sd-company-value">{formatCurrency(sale.company_share)}</div>
            </div>
          )}

          {/* Notes */}
          <div className="card">
            <h3 className="detail-section-title">Notes</h3>
            <div className="note-add-wrap" style={{ borderTop: 'none', paddingTop: 0 }}>
              <textarea
                className="input note-input"
                placeholder="Add a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
              />
              <div className="note-add-actions">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!newNote.trim() || savingNote}
                  onClick={handleAddNote}
                >
                  {savingNote ? 'Saving...' : 'Save note'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Sidebar */}
        <div className="sd-right">
          {/* Sale meta */}
          <div className="card card-sm">
            <p className="section-label" style={{ marginBottom: 12 }}>Sale Details</p>
            <div className="lead-meta-list">
              <div className="lead-meta-item">
                <span className="lead-meta-label">Stage</span>
                <SaleStageBadge stage={sale.stage} />
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Agent</span>
                <span className="lead-meta-value">{sale.agent_name || 'Agent'}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Unit</span>
                <span className="lead-meta-value" style={{ textTransform: 'capitalize' }}>{sale.unit_type} - {sale.sale_type}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Sales Value</span>
                <span className="lead-meta-value accent">{formatCurrency(sale.total_sales_value)}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Booking Date</span>
                <span className="lead-meta-value">{sale.booking_date ? formatDate(sale.booking_date) : '—'}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Created</span>
                <span className="lead-meta-value">{formatDate(sale.created_at)}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Last update</span>
                <span className="lead-meta-value">{timeAgo(sale.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Activity timeline */}
          <div className="card card-sm">
            <p className="section-label" style={{ marginBottom: 16 }}>Activity</p>
            {activity.length === 0 ? (
              <p className="sd-no-activity">No activity yet.</p>
            ) : (
              <div className="activity-timeline">
                {activity.map((act, i) => (
                  <div key={act.id} className="activity-item">
                    <div className="activity-icon-wrap">
                      <div className={`activity-icon activity-icon--${act.type}`}>
                        <ActivityTypeIcon type={act.type} />
                      </div>
                      {i < activity.length - 1 && <div className="activity-line" />}
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">{act.text}</p>
                      <div className="activity-meta">
                        <span className="activity-actor">{act.actor_name}</span>
                        <span className="activity-time">{timeAgo(act.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {showEdit && (
        <SaleEditModal
          sale={sale}
          users={users}
          currentUser={currentUser}
          onClose={() => setShowEdit(false)}
          onSaved={handleEdited}
        />
      )}

      {/* Delete confirmation */}
      {showDelete && (
        <DeleteSaleModal
          sale={sale}
          onClose={() => setShowDelete(false)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Stage advance confirmation */}
      {showStageConfirm && (
        <Modal title="Advance Stage" size="sm" onClose={() => setShowStageConfirm(null)}>
          <div className="modal-body">
            <p className="delete-msg">
              Move this sale to <strong>{STAGE_LABELS[showStageConfirm]}</strong>?
            </p>
            <p className="delete-hint">
              Stage changes cannot be reversed.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={() => setShowStageConfirm(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => handleStageAdvance(showStageConfirm)}>
              Confirm
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}


/* ─── Edit Modal ──────────────────────────────────────────────────────────── */

function SaleEditModal({ sale, users, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_name: sale.client_name || '',
    pan_number: sale.pan_number || '',
    unit_type: sale.unit_type || 'flat',
    sale_type: sale.sale_type || 'fresh',
    project_name: sale.project_name || '',
    size: sale.size != null ? String(sale.size) : '',
    config: sale.config || '',
    price_per_sqft: sale.price_per_sqft ? String(sale.price_per_sqft) : '',
    plc: sale.plc ? String(sale.plc) : '',
    other_charges: sale.other_charges ? String(sale.other_charges) : '',
    total_sales_value: String(sale.total_sales_value || ''),
    brokerage_type: sale.brokerage_type || 'percentage',
    brokerage_percentage: sale.brokerage_percentage ? String(sale.brokerage_percentage) : '',
    brokerage_amount: sale.brokerage_type === 'lumpsum' ? String(sale.brokerage_amount || '') : '',
    brokerage_remark: sale.brokerage_remark || '',
    customer_passback: String(sale.customer_passback || ''),
    broker_passback: String(sale.broker_passback || ''),
    brokerage_received: String(sale.brokerage_received || ''),
    agent_share_type: sale.agent_share_type || 'percentage',
    agent_share_percentage: sale.agent_share_percentage ? String(sale.agent_share_percentage) : '',
    agent_share_amount: sale.agent_share_type === 'lumpsum' ? String(sale.agent_share_amount || '') : '',
    agent_share_remark: sale.agent_share_remark || '',
    mis: sale.mis != null ? String(sale.mis) : '0',
    booking_date: sale.booking_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setNum = (field) => (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) setForm(f => ({ ...f, [field]: val }));
  };
  const setInt = (field) => (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9]+$/.test(val)) setForm(f => ({ ...f, [field]: val }));
  };

  // Computed values (same logic as SaleCreate)
  const tsv = parseInt(form.total_sales_value) || 0;
  const brokerageAmount = useMemo(() => {
    if (form.brokerage_type === 'percentage') {
      const pct = parseFloat(form.brokerage_percentage) || 0;
      return Math.floor(tsv * pct / 100);
    }
    return parseInt(form.brokerage_amount) || 0;
  }, [form.brokerage_type, form.brokerage_percentage, form.brokerage_amount, tsv]);

  const customerPassback = parseInt(form.customer_passback) || 0;
  const brokerPassback = parseInt(form.broker_passback) || 0;
  const brokerageEarned = brokerageAmount - customerPassback - brokerPassback;

  const agentShareAmount = useMemo(() => {
    if (form.agent_share_type === 'percentage') {
      const pct = parseFloat(form.agent_share_percentage) || 0;
      return Math.floor(Math.max(brokerageEarned, 0) * pct / 100);
    }
    return parseInt(form.agent_share_amount) || 0;
  }, [form.agent_share_type, form.agent_share_percentage, form.agent_share_amount, brokerageEarned]);

  const companyShare = brokerageEarned - agentShareAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updates = {};
      if (form.client_name !== sale.client_name) updates.client_name = form.client_name;
      if (form.pan_number.toUpperCase() !== sale.pan_number) updates.pan_number = form.pan_number.toUpperCase();
      if (form.unit_type !== sale.unit_type) updates.unit_type = form.unit_type;
      if (form.sale_type !== sale.sale_type) updates.sale_type = form.sale_type;
      if (form.project_name !== (sale.project_name || '')) updates.project_name = form.project_name || null;
      if (form.size !== (sale.size != null ? String(sale.size) : '')) {
        updates.size = form.size ? parseFloat(form.size) : null;
      }
      if (form.config !== (sale.config || '')) updates.config = form.config || null;
      if (form.price_per_sqft !== (sale.price_per_sqft ? String(sale.price_per_sqft) : '')) {
        updates.price_per_sqft = form.price_per_sqft ? parseFloat(form.price_per_sqft) : null;
      }
      if (form.plc !== (sale.plc ? String(sale.plc) : '')) {
        updates.plc = form.plc ? parseFloat(form.plc) : null;
      }
      if (form.other_charges !== (sale.other_charges ? String(sale.other_charges) : '')) {
        updates.other_charges = form.other_charges ? parseFloat(form.other_charges) : null;
      }
      if (form.total_sales_value !== String(sale.total_sales_value)) {
        updates.total_sales_value = parseInt(form.total_sales_value);
      }
      if (form.brokerage_type !== sale.brokerage_type) updates.brokerage_type = form.brokerage_type;
      if (form.brokerage_type === 'percentage') {
        if (form.brokerage_percentage !== (sale.brokerage_percentage ? String(sale.brokerage_percentage) : '')) {
          updates.brokerage_percentage = parseFloat(form.brokerage_percentage);
        }
      } else {
        if (form.brokerage_amount !== String(sale.brokerage_amount)) {
          updates.brokerage_amount = parseInt(form.brokerage_amount);
        }
        updates.brokerage_remark = form.brokerage_remark;
      }
      if (form.customer_passback !== String(sale.customer_passback)) updates.customer_passback = parseInt(form.customer_passback);
      if (form.broker_passback !== String(sale.broker_passback)) updates.broker_passback = parseInt(form.broker_passback);
      if (form.brokerage_received !== String(sale.brokerage_received || '')) updates.brokerage_received = parseInt(form.brokerage_received) || 0;
      if (form.agent_share_type !== sale.agent_share_type) updates.agent_share_type = form.agent_share_type;
      if (form.agent_share_type === 'percentage') {
        if (form.agent_share_percentage !== (sale.agent_share_percentage ? String(sale.agent_share_percentage) : '')) {
          updates.agent_share_percentage = parseFloat(form.agent_share_percentage);
        }
      } else {
        if (form.agent_share_amount !== String(sale.agent_share_amount)) {
          updates.agent_share_amount = parseInt(form.agent_share_amount);
        }
        updates.agent_share_remark = form.agent_share_remark;
      }

      // MIS (admin only)
      if (currentUser?.role === 'admin') {
        const misVal = parseInt(form.mis) || 0;
        if (misVal !== (sale.mis || 0)) updates.mis = misVal;
      }
      // Booking date
      if (form.booking_date !== (sale.booking_date || '')) {
        updates.booking_date = form.booking_date;
      }

      if (Object.keys(updates).length > 0) {
        await salesApi.update(sale.id, updates);
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || d).join('. '));
      } else {
        setError(detail || 'Failed to update sale.');
      }
    } finally {
      setSaving(false);
    }
  };

  const needsProjectName = form.unit_type === 'flat' || form.unit_type === 'shop';

  return (
    <Modal title="Edit Sale" onClose={onClose} size="lg">
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <p className="section-label" style={{ marginBottom: 12 }}>Client & Project</p>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Client Name *</label>
              <input className="input" value={form.client_name} onChange={set('client_name')} required />
            </div>
            <div className="form-group">
              <label className="section-label">PAN *</label>
              <input className="input" value={form.pan_number} onChange={set('pan_number')} required style={{ textTransform: 'uppercase' }} />
            </div>
          </div>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Booking Date *</label>
              <input className="input" type="date" value={form.booking_date} onChange={set('booking_date')} required />
            </div>
          </div>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Unit Type *</label>
              <select className="input sd-select" value={form.unit_type} onChange={set('unit_type')}>
                {UNIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="section-label">Sale Type *</label>
              <select className="input sd-select" value={form.sale_type} onChange={set('sale_type')}>
                {SALE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Project Name{needsProjectName ? ' *' : ''}</label>
              <input className="input" value={form.project_name} onChange={set('project_name')} required={needsProjectName} />
            </div>
            <div className="form-group">
              <label className="section-label">Total Sales Value *</label>
              <input className="input" value={form.total_sales_value} onChange={setInt('total_sales_value')} required />
            </div>
          </div>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Size (sq. ft.)</label>
              <input className="input" value={form.size} onChange={setNum('size')} placeholder="e.g. 1200" />
            </div>
            <div className="form-group">
              <label className="section-label">Config</label>
              <input className="input" value={form.config} onChange={set('config')} />
            </div>
          </div>

          <p className="section-label" style={{ marginTop: 16, marginBottom: 12 }}>Brokerage</p>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Type</label>
              <select className="input sd-select" value={form.brokerage_type} onChange={set('brokerage_type')}>
                <option value="percentage">Percentage</option>
                <option value="lumpsum">Lumpsum</option>
              </select>
            </div>
            {form.brokerage_type === 'percentage' ? (
              <div className="form-group">
                <label className="section-label">% *</label>
                <input className="input" value={form.brokerage_percentage} onChange={setNum('brokerage_percentage')} required />
              </div>
            ) : (
              <div className="form-group">
                <label className="section-label">Amount *</label>
                <input className="input" value={form.brokerage_amount} onChange={setInt('brokerage_amount')} required />
              </div>
            )}
          </div>
          {form.brokerage_type === 'lumpsum' && (
            <div className="form-group">
              <label className="section-label">Remark *</label>
              <input className="input" value={form.brokerage_remark} onChange={set('brokerage_remark')} required />
            </div>
          )}
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Customer Passback *</label>
              <input className="input" value={form.customer_passback} onChange={setInt('customer_passback')} required />
            </div>
            <div className="form-group">
              <label className="section-label">Broker Passback *</label>
              <input className="input" value={form.broker_passback} onChange={setInt('broker_passback')} required />
            </div>
          </div>

          {form.brokerage_type === 'percentage' && brokerageAmount > 0 && (
            <div className="sc-computed-row">
              <span className="sc-computed-label">Brokerage Amount</span>
              <span className="sc-computed-value">{formatCurrency(brokerageAmount)}</span>
            </div>
          )}
          <div className={`sc-computed-row sc-computed-row--highlight ${brokerageEarned < 0 ? 'sc-computed-row--error' : ''}`}>
            <span className="sc-computed-label">Brokerage Earned</span>
            <span className="sc-computed-value">{formatCurrency(brokerageEarned)}</span>
          </div>
          {brokerageEarned < 0 && (
            <p className="sc-validation-error">Passbacks exceed brokerage amount</p>
          )}

          <div className="sd-form-row" style={{ marginTop: 12 }}>
            <div className="form-group">
              <label className="section-label">Brokerage Received</label>
              <input className="input" value={form.brokerage_received} onChange={setInt('brokerage_received')} placeholder="0" />
            </div>
          </div>

          <p className="section-label" style={{ marginTop: 16, marginBottom: 12 }}>Agent Share</p>
          <div className="sd-form-row">
            <div className="form-group">
              <label className="section-label">Type</label>
              <select className="input sd-select" value={form.agent_share_type} onChange={set('agent_share_type')}>
                <option value="percentage">Percentage</option>
                <option value="lumpsum">Lumpsum</option>
              </select>
            </div>
            {form.agent_share_type === 'percentage' ? (
              <div className="form-group">
                <label className="section-label">% *</label>
                <input className="input" value={form.agent_share_percentage} onChange={setNum('agent_share_percentage')} required />
              </div>
            ) : (
              <div className="form-group">
                <label className="section-label">Amount *</label>
                <input className="input" value={form.agent_share_amount} onChange={setInt('agent_share_amount')} required />
              </div>
            )}
          </div>
          {form.agent_share_type === 'lumpsum' && (
            <div className="form-group">
              <label className="section-label">Remark *</label>
              <input className="input" value={form.agent_share_remark} onChange={set('agent_share_remark')} required />
            </div>
          )}

          {form.agent_share_type === 'percentage' && agentShareAmount > 0 && (
            <div className="sc-computed-row">
              <span className="sc-computed-label">Agent Share Amount</span>
              <span className="sc-computed-value">{formatCurrency(agentShareAmount)}</span>
            </div>
          )}

          {currentUser?.role === 'admin' && (
            <div className={`sc-computed-row sc-computed-row--highlight ${companyShare < 0 ? 'sc-computed-row--error' : ''}`}>
              <span className="sc-computed-label">Company Share</span>
              <span className="sc-computed-value">{formatCurrency(companyShare)}</span>
            </div>
          )}
          {companyShare < 0 && currentUser?.role === 'admin' && (
            <p className="sc-validation-error">Agent share exceeds brokerage earned</p>
          )}

          {currentUser?.role === 'admin' && (
            <>
              <p className="section-label" style={{ marginTop: 16, marginBottom: 12 }}>MIS Deduction</p>
              <div className="form-group">
                <label className="section-label">MIS Amount</label>
                <input className="input" value={form.mis} onChange={setInt('mis')} placeholder="0" />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || brokerageEarned < 0 || (currentUser?.role === 'admin' && companyShare < 0)}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ─── Delete Modal ────────────────────────────────────────────────────────── */

function DeleteSaleModal({ sale, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await salesApi.delete(sale.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete sale.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Delete Sale" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete the sale for <strong>{sale.client_name}</strong>?
        </p>
        <p className="delete-hint">
          This sale will be archived and no longer visible in the sales list.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Sale'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Sub-components ─────────────────────────────────────────────────────── */

function DetailField({ label, value, monospace, accent }) {
  return (
    <div className="detail-field">
      <span className="detail-field-label">{label}</span>
      <span className={`detail-field-value ${monospace ? 'monospace' : ''} ${accent ? 'accent' : ''}`}>
        {value || '\u2014'}
      </span>
    </div>
  );
}

function ActivityTypeIcon({ type }) {
  if (type === 'note')   return <NoteIcon />;
  if (type === 'stage')  return <StageIcon />;
  if (type === 'edit')   return <EditActivityIcon />;
  if (type === 'delete') return <DeleteActivityIcon />;
  return <SparkIcon />;
}


/* ─── Page-specific icons ───────────────────────────────────────────────── */

function CheckIcon() { return <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function NoteIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><rect x="2" y="1.5" width="10" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 5h4M5 7.5h4M5 10h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>; }
function StageIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 7l2 2 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function EditActivityIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M10 1.5l2.5 2.5L4.5 12H2v-2.5L10 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function SparkIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1l1.3 4.3H13l-3.65 2.65 1.4 4.3L7 9.5l-3.75 2.75 1.4-4.3L1 5.3h4.7L7 1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>; }
function DeleteActivityIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M3 4h8M5 4V3a1 1 0 011-1h2a1 1 0 011 1v1M4.5 4v7a1 1 0 001 1h3a1 1 0 001-1V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
